import {
  Avatar,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import { SECURITY_FALLBACKS } from '../../../../../../corelogic/domain/constants/displayFallbacks.constants';
import { TOURNAMENT_DEFAULTS } from '../../../../../../corelogic/domain/constants/tournament.constant';
import { TournamentParticipant } from '../../../../../../corelogic/domain/models/tournament.model';
import { CODE_TO_COUNTRY } from '../../../../../../corelogic/domain/utils/country.utils';
import {
  getEnrichedOverallRankings,
  isFMMAPublicTournament,
} from '../../../../../../corelogic/domain/utils/tournament.utils';
import { SafeText } from '../../../../../../corelogic/domain/utils/xssProtection.utils';
import RankIcon from '../../Shared/GetRankIcon';

const styles = {
  tableContainer: {
    bgcolor: 'rgba(20, 20, 20, 0.95)',
    backgroundImage:
      'linear-gradient(to bottom, rgba(10, 10, 10, 0.9), rgba(0, 0, 0, 0.95))',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
    border: '1px solid rgba(30, 30, 30, 0.8)',
    width: '100%',
    height: '100%',
    overflow: 'auto',
    pt: 0,
    gap: 2,
  },
  tableHead: {
    '& .MuiTableCell-head': {
      bgcolor: 'rgba(0, 0, 0, 0.9)',
      color: '#ffffff',
      fontFamily: 'Rubik Mono One',
      fontSize: 'clamp(0.7rem, 1vw, 0.8rem)',
      borderBottom: '2px solid rgba(140, 0, 0, 0.7)',
      padding: '8px 12px',
      letterSpacing: '0.5px',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
  },
  tableRow: (isCurrentUser: boolean) => ({
    bgcolor: isCurrentUser ? 'rgba(100, 0, 0, 0.2)' : 'transparent',
    '&:nth-of-type(odd)': {
      bgcolor: isCurrentUser
        ? 'rgba(100, 0, 0, 0.25)'
        : 'rgba(20, 20, 20, 0.5)',
    },
    '&:hover': {
      bgcolor: isCurrentUser ? 'rgba(100, 0, 0, 0.3)' : 'rgba(30, 30, 30, 0.7)',
    },
    transition: 'background-color 0.2s',
    height: '54px',
  }),
  tableCell: {
    color: '#e0e0e0',
    borderBottom: '1px solid rgba(40, 40, 40, 0.8)',
    padding: '6px 12px',
  },
  playerCell: {
    display: 'flex',
    alignItems: 'left',
    gap: 1.5,
    justifyContent: 'space-between',
  },
  playerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexGrow: 1,
    paddingLeft: '6px',
    justifyContent: 'space-between',
  },
  playerName: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    fontFamily: 'Bebas Neue, sans-serif',
    letterSpacing: '0.5px',
    fontSize: 'clamp(0.85rem, 1vw, 0.95rem)',
  },
  pointsCell: {
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: 'clamp(0.8rem, 1.2vw, 0.9rem)',
    textShadow: '0 0 4px rgba(140, 0, 0, 0.6)',
  },
  winnerChip: {
    bgcolor: 'rgba(255, 215, 0, 0.2)',
    color: '#FFD700',
    border: '1px solid rgba(255, 215, 0, 0.5)',
    fontWeight: 'bold',
    fontSize: '0.65rem',
    height: 20,
  },
  userIndicator: {
    color: '#900000',
    fontWeight: 'bold',
    fontSize: '0.65rem',
    bgcolor: 'rgba(140, 0, 0, 0.15)',
    border: '1px solid rgba(140, 0, 0, 0.3)',
    height: 20,
  },
  flag: {
    width: '20px',
    height: '14px',
    borderRadius: '2px',
    display: 'block',
    objectFit: 'cover' as const,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
  },
  nationality: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: 4,
    color: '#cccccc',
    backgroundImage:
      'linear-gradient(to bottom, rgba(10, 10, 10, 0.9), rgba(0, 0, 0, 0.95))',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
    border: '1px solid rgba(30, 30, 30, 0.8)',
    height: '200px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  seasonInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
  },
  fightsInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
  },
  avatar: {
    width: 32,
    height: 32,
    border: '1px solid rgba(140, 0, 0, 0.5)',
    backgroundColor: 'rgba(20, 20, 20, 0.7)',
  },
};

interface OverallRankingsProps {
  rankings: TournamentParticipant[];
  username: string;
  totalSeasons: number;
  isCompleted: boolean;
  tournamentName?: string;
  tournamentCreatorName?: string;
  tournamentPrivate?: boolean;
}

const OverallRankings = ({
  rankings,
  username,
  totalSeasons,
  isCompleted,
  tournamentName,
  tournamentCreatorName,
  tournamentPrivate,
}: OverallRankingsProps) => {
  // Get rankings with all necessary info
  const enrichedRankings = getEnrichedOverallRankings(
    rankings,
    totalSeasons,
    username,
    isCompleted,
  );

  // Check if this is a public FMMA tournament with no creator
  const isFMMAPublic =
    tournamentName &&
    isFMMAPublicTournament(
      tournamentName,
      tournamentCreatorName,
      tournamentPrivate,
    );

  // Helper function to display rank for FMMA public tournaments
  const displayRank = (rank: number) => {
    if (isFMMAPublic && rank > TOURNAMENT_DEFAULTS.MAX_GLOBAL_DISPLAYED_USERS) {
      return `${TOURNAMENT_DEFAULTS.MAX_GLOBAL_DISPLAYED_USERS}+`;
    }
    return <RankIcon rank={rank - 1} />; // RankIcon expects 0-based index
  };

  if (enrichedRankings.length === 0) {
    return (
      <Box sx={styles.emptyState}>
        <Typography variant="h6" sx={{ fontFamily: 'Rubik Mono One', mb: 2 }}>
          No Participants Yet
        </Typography>
        <Typography variant="body1">
          Be the first to join this tournament and compete!
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Table stickyHeader size="small">
        <TableHead sx={styles.tableHead}>
          <TableRow>
            <TableCell width="10%" align="center">
              RANK
            </TableCell>
            <TableCell width="30%" align="left" sx={{ pl: 2 }}>
              PLAYER
            </TableCell>
            <TableCell width="15%" align="center">
              NATION
            </TableCell>
            <TableCell width="15%" align="center">
              SEASONS
            </TableCell>
            <TableCell width="15%" align="center">
              FIGHTS
            </TableCell>
            <TableCell width="15%" align="right">
              POINTS
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {enrichedRankings.map((participant, index) => {
            const isCurrentUser = participant.username === username;
            const isWinner = isCompleted && index === 0;
            return (
              <TableRow
                key={participant.username}
                sx={styles.tableRow(isCurrentUser)}
              >
                <TableCell sx={styles.tableCell} align="center">
                  {displayRank(participant.rank)}
                </TableCell>
                <TableCell sx={styles.tableCell}>
                  <Box sx={styles.playerCell}>
                    <Avatar
                      src={participant.profilePicture}
                      alt={participant.username}
                      sx={styles.avatar}
                    >
                      <SafeText
                        text={participant.username?.charAt(0).toUpperCase()}
                        fallback={SECURITY_FALLBACKS.USER_INITIAL}
                      />
                    </Avatar>

                    <Box sx={styles.playerInfo}>
                      <Box sx={styles.playerName}>
                        <SafeText
                          text={participant.username}
                          fallback={SECURITY_FALLBACKS.USERNAME}
                        />
                        <Box
                          sx={{ display: 'flex', gap: 1, marginLeft: 'auto' }}
                        >
                          {isCurrentUser && (
                            <Chip
                              size="small"
                              label="YOU"
                              sx={styles.userIndicator}
                            />
                          )}
                          {isWinner && (
                            <Chip
                              size="small"
                              label="WINNER"
                              sx={styles.winnerChip}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={styles.tableCell} align="center">
                  <Box sx={styles.nationality}>
                    {participant.nationality && (
                      <Box sx={styles.nationality}>
                        <Tooltip
                          title={
                            CODE_TO_COUNTRY[participant.nationality] ||
                            participant.nationality
                          }
                        >
                          <Box>
                            <ReactCountryFlag
                              countryCode={participant.nationality}
                              svg
                              style={styles.flag}
                              title={
                                CODE_TO_COUNTRY[participant.nationality] ||
                                participant.nationality
                              }
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={styles.tableCell} align="center">
                  <Box sx={styles.seasonInfo}>
                    {totalSeasons === 0
                      ? participant.participatedSeasons
                      : `${participant.participatedSeasons} / ${totalSeasons}`}
                  </Box>
                </TableCell>
                <TableCell sx={styles.tableCell} align="center">
                  <Box sx={styles.fightsInfo}>
                    {totalSeasons === 0
                      ? participant.completedFights
                      : `${participant.completedFights} / ${participant.totalPossibleFights}`}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ ...styles.tableCell, ...styles.pointsCell }}
                  align="right"
                >
                  {participant.points}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OverallRankings;
